import { ApiService } from '../services/api'
import { AppState } from '../stores/app-state'
import { DocumentManager } from './document-manager'
import { DataManager } from './data-manager'
import { SelectionManager } from './selection-manager'
import { ModalManager } from './modal-manager'
import { TextProcessingEventManager } from './text-processing-event-manager'
import { ProcessingManager } from './processing-manager'
import { SelectionPreviewRenderer } from './selection-preview-renderer'
import { Toast } from '../components/toast'
import { PDFViewer } from '../components/pdf-viewer-legacy'
import { FileUpload } from '../components/file-upload'
import { ProjectManager } from '../components/project-manager'
import { ProjectDocuments } from '../components/project-documents'
import { PDFSelector } from '../components/pdf-selector'
import { PlotViewer } from '../components/plot-viewer'
import { UIManager } from './ui-manager'

export class StepRenderer {
  private apiService: ApiService
  private appState: AppState
  private documentManager: DocumentManager
  private dataManager: DataManager
  private _selectionManager: SelectionManager
  private _modalManager: ModalManager
  private textProcessingEventManager: TextProcessingEventManager
  private processingManager: ProcessingManager
  private selectionPreviewRenderer: SelectionPreviewRenderer
  private toast: Toast
  private uiManager: UIManager
  private pdfViewer: PDFViewer | null = null
  // private fileUpload: FileUpload | null = null // Not used directly
  private plotViewer: PlotViewer | null = null
  private renderTimeout: NodeJS.Timeout | null = null

  constructor(
    apiService: ApiService,
    appState: AppState,
    documentManager: DocumentManager,
    dataManager: DataManager,
    selectionManager: SelectionManager,
    modalManager: ModalManager,
    textProcessingEventManager: TextProcessingEventManager,
    processingManager: ProcessingManager,
    selectionPreviewRenderer: SelectionPreviewRenderer,
    toast: Toast,
    uiManager: UIManager
  ) {
    this.apiService = apiService
    this.appState = appState
    this.documentManager = documentManager
    this.dataManager = dataManager
    this._selectionManager = selectionManager
    this._modalManager = modalManager
    this.textProcessingEventManager = textProcessingEventManager
    this.processingManager = processingManager
    this.selectionPreviewRenderer = selectionPreviewRenderer
    this.toast = toast
    this.uiManager = uiManager
  }

  public setPDFViewer(pdfViewer: PDFViewer | null): void {
    this.pdfViewer = pdfViewer
  }

  public setFileUpload(_fileUpload: FileUpload | null): void {
    // fileUpload is not stored as a property, just used for initialization
  }

  public setPlotViewer(plotViewer: PlotViewer | null): void {
    this.plotViewer = plotViewer
  }

  public createStandaloneSelectionsGallery(container: HTMLElement, selections: any[]): void {
    const existingGallery = container.querySelector('.selections-gallery')
    if (existingGallery) {
      existingGallery.remove()
    }
    
    import('@/components/selections-gallery').then(({ SelectionsGallery }) => {
      new SelectionsGallery(container, {
        onSelectionRemove: (id) => {
          this.appState.removeSelection(id)
          this.apiService.deleteSelection(id).catch(error => console.error('Failed to delete selection:', error))
        },
        onSelectionNavigate: (id) => {
          const selection = selections.find(s => s.id === id)
          if (selection) {
            this.documentManager.loadDocumentForSelection(selection).then(() => {
              setTimeout(() => {
                if (this.pdfViewer) {
                  this.pdfViewer.goToSelection(id)
                }
              }, 500)
            }).catch((error: any) => {
              console.error('Failed to load PDF for selection navigation:', error)
              this.toast.error('Failed to navigate to selection')
            })
          }
        },
        onRetryThumbnail: (id) => {
          const selection = selections.find(s => s.id === id)
          if (selection) {
            this.documentManager.loadDocumentForSelection(selection).then(() => {
              if (this.pdfViewer) {
                this.pdfViewer.retryThumbnailGeneration(id)
              }
            })
          }
        },
        getPDFName: (pdfId) => this.documentManager.getPDFNameById(pdfId)
      }).updateSelections(selections)
    })
  }

  /**
   * Debounced render to prevent multiple rapid re-renders
   */
  private debouncedRenderStepContent(): void {
    // Clear any existing timeout
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout)
    }
    
    // Set a new timeout for the render
    this.renderTimeout = setTimeout(() => {
      console.log('🔄 Executing debounced render step content')
      this.renderStepContent()
      this.renderTimeout = null
    }, 150) // 150ms debounce
  }

  public async renderStepContent(): Promise<void> {
    console.log('🎯 StepRenderer.renderStepContent() called')
    const stepContent = document.getElementById('step-content')
    if (!stepContent) {
      console.error('❌ StepRenderer: step-content element not found!')
      return
    }

    const currentStep = this.appState.getCurrentStep()
    const isAdvancedMode = this.appState.getAdvancedMode()
    
    console.log('🔄 StepRenderer.renderStepContent() - Current state:', {
      step: currentStep,
      isAdvancedMode,
      stepContentExists: !!stepContent
    })

    // Clean up components when navigating away from their steps
    if (this.pdfViewer) {
      console.log('🧹 Cleaning up PDF viewer before step change')
      this.pdfViewer.clearState()
      this.pdfViewer = null
    }

    if (currentStep !== 5 && this.plotViewer) {
      console.log('🧹 Cleaning up PlotViewer instance (navigating away from plotting step)')
      this.plotViewer = null
    }

    console.log('🔄 Rendering step content for step:', {
      step: currentStep,
      hasCoordinates: this.appState.getCoordinates().length > 0,
      projectId: this.appState.getCurrentProject()?.id,
      isAdvancedMode
    })
    stepContent.innerHTML = ''

    // Ensure coordinates are restored when needed
    const currentProject = this.appState.getCurrentProject()
    if (currentProject && ((currentStep === 4 && !isAdvancedMode) || (currentStep === 3 && isAdvancedMode)) && this.appState.getCoordinates().length === 0) {
      console.log('🔄 No coordinates found in plotting step, attempting restore')
      this.appState.restoreCoordinates(currentProject.id)
      console.log('🔄 After restore attempt, coordinates count:', this.appState.getCoordinates().length)
    }

    if (isAdvancedMode) {
      // Advanced mode: 3 steps
      switch (currentStep) {
        case 1:
          this.renderProjectStep(stepContent)
          break
        case 2:
          await this.renderUnifiedInputStep(stepContent)
          break
        case 3:
          await this.renderAdvancedPlotStep(stepContent)
          break
        default:
          console.error('Unknown advanced mode step:', currentStep)
      }
    } else {
      // Classic mode: 4 steps (combined Documents + Snippets into Input & Capture)
      switch (currentStep) {
        case 1:
          this.renderProjectStep(stepContent)
          break
        case 2:
          await this.renderUnifiedInputStep(stepContent)
          break
        case 3:
          if (this.appState.getSelections().length === 0) {
            await this.dataManager.loadSelectionsForCurrentProject()
          }
          await this.renderFormattingStep(stepContent)
          break
        case 4:
          this.renderPlottingStep(stepContent)
          break
        default:
          console.error('Unknown classic mode step:', currentStep)
      }
    }
  }

  public renderProjectStep(container: HTMLElement): void {
    console.log('🎯 StepRenderer.renderProjectStep() called')
    const currentProject = this.appState.getCurrentProject()
    const canAdvance = !!currentProject
    
    console.log('🔄 renderProjectStep - Current state:', {
      currentProject: currentProject?.id,
      canAdvance,
      containerExists: !!container
    })

    container.innerHTML = `
      <div class="max-w-4xl mx-auto">
        <div class="card">
          <div class="card-header">
            <h2 class="text-xl font-semibold text-gray-900">Project Management</h2>
            <p class="text-sm text-gray-600 mt-1">Select an existing project or create a new one to organize your PDFs and text selections</p>
          </div>
          ${canAdvance ? `
            <div class="card-header border-t bg-gray-50">
              <div class="flex justify-end">
                <button class="btn btn-primary" id="step-next-top">Next: Documents →</button>
              </div>
            </div>
          ` : ''}
          <div class="card-body">
            <div id="project-manager"></div>
          </div>
        </div>
        <div class="step-navigation-bottom">
          <div class="nav-left"></div>
          <div class="nav-right">
            ${canAdvance ? `<button class="btn btn-primary" id="step-next-bottom">Next: Documents →</button>` : ''}
          </div>
        </div>
      </div>
    `
    new ProjectManager(
      document.getElementById('project-manager')!, 
      this.apiService, 
      this.appState, 
      this.toast,
      () => {
        // Refresh workflow navigation when project changes
        // This will be handled by the event system
        window.dispatchEvent(new CustomEvent('render-step-content'))
      }
    )
  }

  public renderDocumentsStep(container: HTMLElement): void {
    const project = this.appState.getCurrentProject()
    const canAdvance = project && project.document_count > 0

    container.innerHTML = `
      <div class="max-w-4xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div class="card">
            <div class="card-header">
              <h2 class="text-lg font-semibold text-gray-900">Select From Documents</h2>
              <p class="text-sm text-gray-600 mt-1">Upload and select text from PDF, image, or other documents</p>
            </div>
            <div class="card-body">
              <div id="file-upload"></div>
            </div>
          </div>
          <div class="card">
            <div class="card-header">
              <h2 class="text-lg font-semibold text-gray-900">Project Documents</h2>
              <p class="text-sm text-gray-600 mt-1">Manage uploaded documents in this project</p>
            </div>
            <div class="card-body">
              <div id="project-documents"></div>
            </div>
          </div>
        </div>
        <div class="step-navigation-bottom">
          <div class="nav-left"></div>
          <div class="nav-right">
            ${canAdvance ? `<button class="btn btn-primary" id="step-next-bottom">Next: Selections →</button>` : ''}
          </div>
        </div>
      </div>
    `
    new FileUpload(document.getElementById('file-upload')!, this.apiService, this.appState, this.dataManager.onFileUploaded.bind(this.dataManager))
    new ProjectDocuments(document.getElementById('project-documents')!, this.apiService, this.appState, this.toast)
  }

  public async renderSnippetsStep(container: HTMLElement): Promise<void> {
    const selections = this.appState.getSelections()
    const canAdvance = selections.length > 0

    container.innerHTML = `
      <div class="max-w-7xl mx-auto">
        <div class="space-y-6">
          <div class="card">
            <div class="card-header">
              <h2 class="text-lg font-semibold text-gray-900">Select PDF Document</h2>
              <p class="text-sm text-gray-600 mt-1">Choose which PDF to work with for text selection</p>
            </div>
            <div class="card-body">
              <div id="pdf-document-selector"></div>
            </div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div id="pdf-viewer-container" class="card">
              <div class="card-header">
                <h2 class="text-lg font-semibold text-gray-900">PDF Viewer</h2>
                <p class="text-sm text-gray-600 mt-1">Select text areas from your PDF</p>
              </div>
              <div class="card-body">
                <div id="pdf-viewer">
                  <div class="text-center py-8 text-gray-500">
                    <div class="text-4xl mb-3">📄</div>
                    <h3 class="text-lg font-medium mb-2">No PDF Selected</h3>
                    <p class="text-sm">Select a PDF document above to begin</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="card">
              <div class="card-header">
                <h2 class="text-lg font-semibold text-gray-900">Text Selections</h2>
                <p class="text-sm text-gray-600 mt-1">Manage and organize selected text snippets</p>
              </div>
              <div class="card-body">
                <div id="selections-gallery-container-main">
                  <div class="text-center py-8 text-gray-500">
                    <div class="text-4xl mb-3">✂️</div>
                    <h3 class="text-lg font-medium mb-2">No Selections Yet</h3>
                    <p class="text-sm">Click and drag on the PDF to select text areas</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="step-navigation-bottom">
          <div class="nav-left">
            <button class="btn btn-secondary" id="step-prev-bottom">← Previous: Documents</button>
          </div>
          <div class="nav-right">
            ${canAdvance ? `<button class="btn btn-primary" id="step-next-bottom">Next: Descriptions →</button>` : ''}
          </div>
        </div>
      </div>
    `
    new PDFSelector(document.getElementById('pdf-document-selector')!, this.apiService, this.appState, this.toast)
    
    const currentProject = this.appState.getCurrentProject()
    if (currentProject) {
      await this.documentManager.loadProjectDocuments()
      await this.dataManager.loadSelectionsForCurrentProject()
      
      const existingSelections = this.appState.getSelections()
      if (existingSelections.length > 0) {
        const externalContainer = document.getElementById('selections-gallery-container-main')
        if (externalContainer) {
          this.createStandaloneSelectionsGallery(externalContainer, existingSelections)
        }
      }
    }

    const currentFileSnippets = this.appState.getCurrentFile()
    if (currentFileSnippets) {
      this.documentManager.showPDFViewer(currentFileSnippets)
    }
  }


  public renderPlottingStep(container: HTMLElement): void {
    container.innerHTML = `
      <div class="plotting-step-container h-full">
        <div id="plot-viewer-container" class="h-full"></div>
        <div class="step-navigation-bottom">
          <div class="nav-left">
            <button class="btn btn-secondary" id="step-prev-bottom">← Previous: Descriptions</button>
          </div>
          <div class="nav-right"></div>
        </div>
      </div>
    `

    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      try {
        const plotContainer = document.getElementById('plot-viewer-container')
        
        if (!plotContainer) {
          console.error('PlotViewer container not found after DOM update')
          return
        }

        if (!this.plotViewer) {
          // Create new PlotViewer instance
          console.log('📐 Creating new PlotViewer instance')
          this.plotViewer = new PlotViewer(
            plotContainer,
            this.appState,
            this.toast,
            this.apiService
          )
        } else {
          // Refresh existing plot viewer
          console.log('🔄 Refreshing existing PlotViewer')
          this.plotViewer.refresh()
        }
      } catch (error) {
        console.error('Failed to initialize PlotViewer:', error)
        this.toast.error('Failed to initialize plotting interface', 'Please try refreshing the page')
      }
    }, 0)
  }

  public async renderUnifiedInputStep(container: HTMLElement): Promise<void> {
    // Load existing data BEFORE making rendering decisions
    await this.documentManager.loadProjectDocuments()
    await this.dataManager.loadSelectionsForCurrentProject()
    
    // Now check selections after they've been loaded
    const selections = this.appState.getSelections()
    const hasSelections = selections.length > 0

    if (hasSelections) {
      // Show results view if we have selections
      this.renderSelectionResults(container)
    } else {
      // Show landing page for initial choice
      this.renderInputCaptureLanding(container)
    }
  }

  public renderUnifiedInputStepSync(container: HTMLElement): void {
    const selections = this.appState.getSelections()
    const hasSelections = selections.length > 0

    if (hasSelections) {
      // Show results view if we have selections
      this.renderSelectionResults(container)
    } else {
      // Show landing page for initial choice
      this.renderInputCaptureLanding(container)
    }
    
    // Don't reload selections to prevent infinite loop
    console.log('🔄 Sync render completed without reloading selections')
  }

  public async renderAdvancedPlotStep(container: HTMLElement): Promise<void> {
    container.innerHTML = `
      <div class="plotting-step-container h-full">
        <div id="plot-viewer-container" class="h-full"></div>
        <div class="step-navigation-bottom">
          <div class="nav-left">
            <button class="btn btn-secondary" id="step-prev-bottom">← Previous: Input & Capture</button>
          </div>
          <div class="nav-right">
            <!-- This is the final step -->
          </div>
        </div>
      </div>
    `
    
    // REMOVED: Direct edit panel initialization - edit functionality moved to plot left panel
    
    // Use setTimeout to ensure DOM is fully rendered
    setTimeout(() => {
      try {
        const plotContainer = document.getElementById('plot-viewer-container')
        
        if (!plotContainer) {
          console.error('PlotViewer container not found after DOM update')
          return
        }

        if (!this.plotViewer) {
          // Create new PlotViewer instance
          console.log('📐 Creating new PlotViewer instance')
          this.plotViewer = new PlotViewer(
            plotContainer,
            this.appState,
            this.toast,
            this.apiService
          )
        } else {
          // Refresh existing plot viewer
          console.log('🔄 Refreshing existing PlotViewer')
          this.plotViewer.refresh()
        }
      } catch (error) {
        console.error('Failed to initialize PlotViewer:', error)
        this.toast.error('Failed to initialize plotting interface', 'Please try refreshing the page')
      }
    }, 0)
    
    // Start background processing with centralized loading modal
    // Note: This will need to be handled by the calling App class
    window.dispatchEvent(new CustomEvent('start-background-processing'))
  }

  public renderInputCaptureLanding(container: HTMLElement): void {
    container.innerHTML = `
      <div class="input-capture-landing">
        <div class="input-capture-title">
          📝 Input & Description Capture
        </div>
        <div class="input-capture-subtitle">
          Choose how you want to gather your metes and bounds descriptions
        </div>
        
        <div class="input-capture-options">
          <div class="input-capture-option" id="choose-upload-documents">
            <div class="input-capture-option-icon">📄</div>
            <div class="input-capture-option-title">Upload Documents</div>
            <div class="input-capture-option-description">
              Upload PDF documents and select text regions to extract descriptions
            </div>
          </div>
          
          <div class="input-capture-option" id="choose-manual-entry">
            <div class="input-capture-option-icon">✍️</div>
            <div class="input-capture-option-title">Manual Entry</div>
            <div class="input-capture-option-description">
              Type or paste your metes and bounds descriptions directly
            </div>
          </div>
        </div>
        
        <div class="step-navigation-bottom">
          <div class="nav-left">
            <button class="btn btn-secondary" id="step-prev-bottom">← Previous: Projects</button>
          </div>
          <div class="nav-right"></div>
        </div>
      </div>
    `
    
    this.setupLandingEventListeners()
  }

  public renderSelectionResults(container: HTMLElement): void {
    const selections = this.appState.getSelections()
    const hasFormattedText = this.appState.getHasFormattedText()
    
    console.log('🎯 renderSelectionResults called with selections:', selections.map(s => ({
      id: s.id,
      document_type: s.document_type,
      document_id: s.document_id
    })))
    
    container.innerHTML = `
      <div class="max-w-4xl mx-auto">
        <div class="selection-results">
          <div class="selection-results-header">
            <h2 class="selection-results-title">
              📋 Captured Descriptions (${selections.length})
            </h2>
            <div class="selection-results-actions">
              <button class="btn btn-outline" id="add-more-selections">
                ➕ Add More
              </button>
            </div>
          </div>
          
          <div class="space-y-4">
            ${selections.map((selection, index) => `
              <div class="selection-result-item" data-selection-id="${selection.id}">
                <div class="selection-result-header">
                  <div class="selection-result-number">${index + 1}</div>
                  <div class="selection-result-actions">
                    <button class="btn btn-sm btn-outline" data-action="edit" data-selection-id="${selection.id}">
                      ✏️ Edit
                    </button>
                    <button class="btn btn-sm btn-danger" data-action="remove" data-selection-id="${selection.id}">
                      🗑️ Remove
                    </button>
                  </div>
                </div>
                <div class="selection-result-content">
                  ${this.selectionPreviewRenderer.renderSelectionPreview(selection)}
                </div>
                <div class="selection-result-meta">
                  <span>📄 ${this.getDocumentName(selection.document_id || '', selection.document_type)}</span>
                  ${selection.document_type !== 'manual' && selection.document_id !== 'manual-entry' ? `
                    <span>📍 Page ${selection.page_number || 1}</span>
                    <span>📐 ${Math.round(selection.width || 0)}×${Math.round(selection.height || 0)}px</span>
                  ` : ''}
                </div>
            </div>
          `).join('')}
          </div>
        </div>
        
        <div class="step-navigation-bottom">
          <div class="nav-left">
            <button class="btn btn-secondary" id="step-prev-bottom">← Previous: Projects</button>
          </div>
          <div class="nav-right">
            ${this.appState.getCoordinates().length > 0 ? `
              <button class="btn btn-primary" id="step-next-bottom">View Plot →</button>
            ` : hasFormattedText || selections.length > 0 ? `
              <button class="btn btn-primary" id="step-next-bottom">${this.appState.getAdvancedMode() ? 'Next: Generate Plot →' : 'Next: Descriptions →'}</button>
            ` : ''}
          </div>
        </div>
      </div>
    `
    
    this.setupSelectionResultsEventListeners()
    
    // Update generate plot button state based on OCR processing status
    if (this.processingManager) {
      this.processingManager.updateGeneratePlotButtonState()
      
      // Start OCR status polling for pending selections
      this.processingManager.startOCRStatusPolling()
    }
  }

  public async renderFormattingStep(container: HTMLElement): Promise<void> {
    const selections = this.appState.getSelections()

    // Check if all selections have been processed (OCR complete)
    const unprocessedSelections = selections.filter(sel => !sel.processed)
    if (unprocessedSelections.length > 0) {
      console.log('⏳ Some selections are still being processed:', unprocessedSelections.length)
      this.toast.info('Processing Selections', 'Please wait while OCR processing completes for all selections')
      
      // Show a processing message instead of the full interface
      container.innerHTML = `
        <div class="max-w-7xl mx-auto">
          <div class="card">
            <div class="card-body text-center py-12">
              <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Processing Selections</h3>
              <p class="text-gray-600 mb-4">OCR is still processing ${unprocessedSelections.length} selection(s). Please wait...</p>
              <div class="text-sm text-gray-500">
                This usually takes a few seconds per selection.
              </div>
            </div>
          </div>
        </div>
      `
      return
    }

    // Retrieve any persisted description list for this project
    const currentProject = this.appState.getCurrentProject()
    let storedList = ''
    if (currentProject && !this.appState.getDescriptionList()) {
      try {
        const workflowData = await this.apiService.getProjectWorkflowData(currentProject.id)
        storedList = workflowData.numbered_list_text || ''
        if (storedList) {
          this.appState.setDescriptionList(storedList)
          this.appState.setHasFormattedText(true)
        }
      } catch (error) {
        console.error('❌ Failed to load description list from database:', error)
      }
    }

    const existingList = this.appState.getDescriptionList() || storedList
    const listExists = existingList.trim().length > 0

    // Get existing coordinates from database or app state
    let coords = this.appState.getCoordinates()
    let coordsStr = coords.join('\n')
    
    // Always try to load coordinates from database for older projects
    // This ensures Metes and Bounds display properly when loading existing projects
    if (currentProject) {
      try {
        console.log('🔍 DEBUG: Loading workflow data for project:', currentProject.id)
        const workflowData = await this.apiService.getProjectWorkflowData(currentProject.id)
        console.log('🔍 DEBUG: Workflow data received:', {
          hasNumberedList: !!workflowData.numbered_list_text,
          hasMetesBounds: !!workflowData.metes_bounds_text,
          metesBoundsLength: workflowData.metes_bounds_text?.length || 0,
          metesBoundsPreview: workflowData.metes_bounds_text?.substring(0, 100) + '...' || 'null'
        })
        
        if (workflowData.metes_bounds_text) {
          coordsStr = workflowData.metes_bounds_text
          coords = coordsStr.split('\n').map(l => l.trim()).filter(l => l)
          this.appState.setCoordinates(coords, true) // Skip persistence since we just loaded from DB
          console.log('🔄 Loaded coordinates from database for formatting step:', coords.length)
          console.log('🔍 DEBUG: Final coordsStr length:', coordsStr.length)
        } else {
          console.log('⚠️ No metes_bounds_text found in workflow data')
        }
      } catch (error) {
        console.error('❌ Failed to load coordinates from database:', error)
      }
    }
    
    const hasCoords = coords.length > 0

    const generateBtnClasses = listExists ? 'btn btn-secondary' : 'btn btn-primary'
    const generateBtnLabel  = listExists ? 'List Generated' : 'Create Ordered List'
    const generateBtnDisabled = listExists ? 'disabled' : ''
    const convertBtnDisabled = listExists ? '' : 'disabled'
    const convertBtnClasses = hasCoords ? 'btn btn-secondary' : (listExists ? 'btn btn-primary' : 'btn btn-secondary')
    const convertBtnLabel = hasCoords ? 'Coordinates Generated' : 'Generate Metes & Bounds'
    const convertBtnFullDisabled = hasCoords ? 'disabled' : convertBtnDisabled

    console.log('🔍 Button state debug:', {
      listExists,
      hasCoords,
      convertBtnDisabled,
      convertBtnFullDisabled,
      convertBtnClasses,
      convertBtnLabel
    })

    container.innerHTML = `
      <div class="max-w-7xl mx-auto">
        <div class="space-y-6">
          <div class="card">
            <div class="card-header">
              <h2 class="text-xl font-semibold text-gray-900">Step 1 – Text Selection Processing</h2>
              <p class="text-sm text-gray-600 mt-1">Clean and normalize text selections for each snippet</p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  ${selections.length} snippets selected
                </span>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selections.filter(s => s.extracted_text).length > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}">
                  ${selections.filter(s => s.extracted_text).length} processed
                </span>
              </div>
            </div>
          </div>
          <div id="ocr-processing-container" class="space-y-6">
            ${selections.length === 0 ? `
              <div class="card">
                <div class="card-body text-center py-12 text-gray-500">
                  <div class="text-4xl mb-3">✂️</div>
                  <h3 class="text-lg font-medium mb-2">No Snippets Available</h3>
                  <p class="text-sm">Go back to Selection Mgmt to select text areas</p>
                </div>
              </div>
            ` : ''}
          </div>
          <!-- Combined description list & coordinate generation -->
          <div id="description-list-tools" class="space-y-6 mt-8">
            <div class="card" id="description-list-card">
              <div class="card-header flex justify-between items-start">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Step 2 – Combine all snippets into numbered list</h3>
                  <p class="text-sm text-gray-600 mt-1">Generates an ordered list from every cleaned snippet.</p>
                  <div class="mt-2 flex flex-wrap gap-1">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                      ${selections.filter(s => {
                        const finalText = s.edited_text || s.extracted_text || ''
                        return finalText.trim().length > 0
                      }).length} text snippets ready
                    </span>
                  </div>
                </div>
                <button id="generate-description-list-btn" class="${generateBtnClasses}" ${generateBtnDisabled}>${listExists ? '✅' : '📝'} ${generateBtnLabel}</button>
              </div>
              <div class="card-body">
                <textarea id="description-list-textarea" class="w-full p-2 border border-gray-300 rounded-md resize-y font-mono text-sm" placeholder="Numbered list will appear here..." style="min-height: 160px;">${existingList}</textarea>
              </div>
            </div>

            <div class="card" id="coordinate-convert-card">
              <div class="card-header flex justify-between items-start">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">Step 3 – Generate Metes and Bounds</h3>
                  <p class="text-sm text-gray-600 mt-1">Converts the numbered list into standard bearing-distance / curve format for surveying.</p>
                </div>
                <button id="convert-to-coords-btn" class="${convertBtnClasses}" ${convertBtnFullDisabled}>${hasCoords ? '✅' : '📐'} ${convertBtnLabel}</button>
              </div>
              <div class="card-body">
                <textarea id="coordinate-list-textarea" class="w-full p-2 border border-gray-300 rounded-md resize-y font-mono text-sm" placeholder="Coordinate strings will appear here..." style="min-height: 160px;">${coordsStr}</textarea>
              </div>
            </div>
          </div>
          <div id="ocr-progress" class="card hidden">
            <div class="card-body">
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
                <div class="flex-1">
                  <h4 class="text-sm font-medium text-gray-900">Processing Text Selection...</h4>
                  <div class="mt-1 text-sm text-gray-600">
                    <span id="progress-text">Initializing...</span>
                  </div>
                  <div class="mt-2 bg-gray-200 rounded-full h-2">
                    <div id="progress-bar" class="bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="step-navigation-bottom">
          <div class="nav-left">
            <button class="btn btn-secondary" id="step-prev-bottom">← Previous: Selections</button>
          </div>
          <div class="nav-right">
                            ${hasCoords ? `<button class="btn btn-primary" id="step-next-bottom">Next: Plot →</button>` : ''}
          </div>
        </div>
      </div>
    `
    
    // Initialize OCR processing - this will need to be handled by the calling App class
    window.dispatchEvent(new CustomEvent('initialize-ocr-processing', { detail: { selections } }))
    
    // Setup text processing event listeners
    this.textProcessingEventManager.setupTextProcessingEventListeners()
    
    // Update coordinate textarea if coordinates were loaded from database
    if (coords.length > 0) {
      setTimeout(() => {
        const coordTextarea = document.getElementById('coordinate-list-textarea') as HTMLTextAreaElement
        if (coordTextarea) {
          console.log('🔍 DEBUG: Updating textarea with coordsStr:', coordsStr.substring(0, 100) + '...')
          coordTextarea.value = coordsStr
          this.autoResizeTextarea(coordTextarea)
          console.log('🔄 Updated coordinate textarea with loaded coordinates:', coords.length)
          console.log('🔍 DEBUG: Textarea value after update:', coordTextarea.value.substring(0, 100) + '...')
        } else {
          console.log('❌ Coordinate textarea not found in DOM')
        }
      }, 50) // Small delay to ensure DOM is ready
    } else {
      console.log('⚠️ No coordinates to display in textarea')
    }
    
    // Attach coordinate textarea listener with auto-resize and auto-save
    const coordTextarea = document.getElementById('coordinate-list-textarea') as HTMLTextAreaElement
    if (coordTextarea && !coordTextarea.dataset.listenerAttached) {
      let saveTimeout: NodeJS.Timeout
      
      coordTextarea.addEventListener('input', () => {
        const newCoords = coordTextarea.value.split('\n').map(l => l.trim()).filter(l => l)
        this.appState.setCoordinates(newCoords)
        this.autoResizeTextarea(coordTextarea) // Auto-resize on input
        
        // Auto-save coordinates to database with debouncing (save 1 second after user stops typing)
        if (saveTimeout) clearTimeout(saveTimeout)
        saveTimeout = setTimeout(async () => {
          const currentProject = this.appState.getCurrentProject()
          if (currentProject && coordTextarea.value.trim()) {
            try {
              await this.apiService.updateProjectWorkflowData(currentProject.id, {
                metes_bounds_text: coordTextarea.value
              })
              console.log('💾 Auto-saved coordinates to database')
            } catch (error) {
              console.error('❌ Failed to auto-save coordinates:', error)
            }
          }
        }, 1000)
      })
      
      // Final save on blur (when user leaves the textarea)
      coordTextarea.addEventListener('blur', async () => {
        if (saveTimeout) clearTimeout(saveTimeout)
        const currentProject = this.appState.getCurrentProject()
        if (currentProject && coordTextarea.value.trim()) {
          try {
            await this.apiService.updateProjectWorkflowData(currentProject.id, {
              metes_bounds_text: coordTextarea.value
            })
            console.log('💾 Saved coordinates to database on blur')
          } catch (error) {
            console.error('❌ Failed to save coordinates:', error)
          }
        }
      })
      
      coordTextarea.dataset.listenerAttached = 'true'
      // Auto-resize for existing content after a short delay to ensure DOM is ready
      setTimeout(() => this.autoResizeTextarea(coordTextarea), 10)
    }

    // Attach description list textarea auto-resize AND auto-save
    const descTextarea = document.getElementById('description-list-textarea') as HTMLTextAreaElement
    if (descTextarea && !descTextarea.dataset.autoResizeAttached) {
      let saveTimeout: NodeJS.Timeout
      
      // Auto-resize on input
      descTextarea.addEventListener('input', () => {
        this.autoResizeTextarea(descTextarea)
        
        // Auto-save with debouncing (save 1 second after user stops typing)
        if (saveTimeout) clearTimeout(saveTimeout)
        saveTimeout = setTimeout(() => {
          this.dataManager.saveDescriptionListToDatabase(descTextarea.value)
        }, 1000)
      })
      
      // Final save on blur (when user leaves the textarea)
      descTextarea.addEventListener('blur', () => {
        if (saveTimeout) clearTimeout(saveTimeout)
        this.saveDescriptionListToDatabase(descTextarea.value)
      })
      
      descTextarea.dataset.autoResizeAttached = 'true'
      // Auto-resize for existing content after a short delay to ensure DOM is ready
      setTimeout(() => this.autoResizeTextarea(descTextarea), 10)
    }
  }

  private autoResizeTextarea(textarea: HTMLTextAreaElement): void {
    // Reset height to auto to get the actual scrollHeight
    textarea.style.height = 'auto'
    
    // Calculate the new height based on content
    const minHeight = 160 // minimum height in pixels
    const maxHeight = 600 // maximum height in pixels
    const newHeight = Math.max(minHeight, Math.min(maxHeight, textarea.scrollHeight + 4))
    
    // Apply the new height
    textarea.style.height = `${newHeight}px`
  }

  private async saveDescriptionListToDatabase(listText: string): Promise<void> {
    const currentProject = this.appState.getCurrentProject()
    if (!currentProject) {
      console.log('⚠️ Cannot save description list: no current project')
      return
    }

    // Don't save if the text hasn't changed
    const currentList = this.appState.getDescriptionList() || ''
    if (currentList === listText) {
      console.log('💾 Description list unchanged, skipping save')
      return
    }

    console.log('💾 Auto-saving description list to database...', {
      projectId: currentProject.id,
      textLength: listText.length,
      textPreview: listText.substring(0, 100) + (listText.length > 100 ? '...' : '')
    })

    try {
      // Update app state first
      this.appState.setDescriptionList(listText)
      if (listText.trim()) {
        this.appState.setHasFormattedText(true)
      }
      
      // Save to database
      await this.apiService.updateProjectWorkflowData(currentProject.id, { 
        numbered_list_text: listText 
      })
      
      console.log('✅ Description list auto-saved successfully')
      
      // Show subtle success indicator
      // Note: This will need to be handled by the calling App class
      window.dispatchEvent(new CustomEvent('show-save-indicator', { detail: { elementId: 'description-list-textarea', type: 'success' } }))
      
    } catch (error) {
      console.error('❌ Failed to auto-save description list:', error)
      
      // Show error indicator
      // Note: This will need to be handled by the calling App class
      window.dispatchEvent(new CustomEvent('show-save-indicator', { detail: { elementId: 'description-list-textarea', type: 'error' } }))
      
      // Don't show toast for auto-save errors to avoid interrupting user experience
      // this.toast.error('Auto-save failed', 'Your changes may not be saved')
    }
  }

  private setupLandingEventListeners(): void {
    const uploadOption = document.getElementById('choose-upload-documents')
    const manualOption = document.getElementById('choose-manual-entry')
    
    uploadOption?.addEventListener('click', () => {
      this._modalManager.openDocumentUploadModal()
    })
    
    manualOption?.addEventListener('click', () => {
      this._modalManager.openManualEntryModal()
    })
  }

  private setupSelectionResultsEventListeners(): void {
    const addMoreBtn = document.getElementById('add-more-selections')
    
    addMoreBtn?.addEventListener('click', () => {
      this._modalManager.openDocumentUploadModal()
    })
    
    // Listen for manual entry modal events (fallback for when ModalManager is not directly accessible)
    // Use a flag to prevent multiple event listeners
    if (!(window as any).manualEntryModalListenerAdded) {
      (window as any).manualEntryModalListenerAdded = true
      window.addEventListener('open-manual-entry-modal', (event: any) => {
        const { prefilledText, documentId, isEditing } = event.detail
        console.log('🎯 Received open-manual-entry-modal event:', { prefilledText: prefilledText?.substring(0, 50) + '...', documentId, isEditing })
        this._modalManager.openManualEntryModal(prefilledText, documentId)
      })
    }
    
    // Listen for selections updated events to refresh UI
    if (!(window as any).selectionsUpdatedListenerAdded) {
      (window as any).selectionsUpdatedListenerAdded = true
      window.addEventListener('selections-updated', (event: any) => {
        console.log('🔄 Received selections-updated event:', event.detail)
        
        // Always refresh the selections gallery if it exists, regardless of current step
        const selectionsGallery = document.querySelector('.selections-gallery')
        if (selectionsGallery) {
          console.log('🔄 Refreshing selections gallery after selections update')
          // Trigger a re-render of the selections gallery
          const selections = this.appState.getSelections()
          if (selections.length > 0) {
            // Find the gallery container and update it
            const galleryContainer = selectionsGallery.querySelector('#gallery-container')
            if (galleryContainer) {
              // Force a re-render by dispatching a custom event that the gallery can listen to
              window.dispatchEvent(new CustomEvent('refresh-selections-gallery', { 
                detail: { selections } 
              }))
            }
          }
        }
        
        // Also re-render the current step if it's the Input & Capture step
        const currentStep = this.appState.getCurrentStep()
        if (currentStep === 2) { // Input & Capture step
          console.log('🔄 Re-rendering Input & Capture step after selections update')
          this.debouncedRenderStepContent()
        }
      })
      
      // Also listen for app-state-refresh events (backup trigger)
      window.addEventListener('app-state-refresh', (event: any) => {
        console.log('🔄 Received app-state-refresh event:', event.detail)
        
        // Force a complete re-render of the current step
        const currentStep = this.appState.getCurrentStep()
        console.log('🔄 Force re-rendering step after app-state-refresh:', currentStep)
        this.debouncedRenderStepContent()
      })
      
      // Listen for immediate UI update events (bypass normal processing)
      window.addEventListener('immediate-ui-update', (event: any) => {
        console.log('🚀 Received immediate-ui-update event:', event.detail)
        
        // Force immediate gallery update without debouncing
        const selectionsGallery = document.querySelector('.selections-gallery')
        if (selectionsGallery) {
          console.log('🚀 IMMEDIATE: Force updating gallery immediately')
          const selections = this.appState.getSelections()
          if (selections.length > 0) {
            window.dispatchEvent(new CustomEvent('refresh-selections-gallery', { 
              detail: { selections } 
            }))
          }
        }
        
        // CRITICAL FIX: Force immediate gallery update by directly calling the gallery
        const galleryInstance = (window as any).selectionsGallery
        if (galleryInstance && galleryInstance.updateSelections) {
          console.log('🚀 IMMEDIATE: Direct gallery update bypassing events')
          galleryInstance.updateSelections(this.appState.getSelections())
        }
        
        // CRITICAL FIX: Force immediate UI update to show thumbnails
        console.log('🚀 IMMEDIATE: Forcing immediate UI update to show thumbnails')
        this.debouncedRenderStepContent()
      })
    }
    
    // Handle individual selection actions
    document.querySelectorAll('[data-action="edit"]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        console.log('🖱️ Edit button clicked!', e.target)
        const selectionId = (e.target as HTMLElement).dataset.selectionId
        console.log('🆔 Selection ID from click:', selectionId)
        if (selectionId) {
          console.log('✅ Calling editSelection for:', selectionId)
          await this._selectionManager.editSelection(selectionId)
        } else {
          console.error('❌ No selection ID found in edit button click')
        }
      })
    })
    
    document.querySelectorAll('[data-action="remove"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const selectionId = (e.target as HTMLElement).dataset.selectionId
        if (selectionId) {
          this._selectionManager.removeSelection(selectionId)
        }
      })
    })

    // Handle "Generate Plot" button click - trigger background processing
    const generatePlotBtn = document.getElementById('step-next-bottom')
    if (generatePlotBtn && generatePlotBtn.textContent?.includes('Generate Plot')) {
      generatePlotBtn.addEventListener('click', async (e) => {
        e.preventDefault()
        console.log('🎯 Generate Plot button clicked - starting background processing')
        
        // Ensure LLM usage manager has the current project ID
        const currentProject = this.appState.getCurrentProject()
        if (currentProject) {
          const { llmUsageManager } = await import('./llm-usage-manager')
          llmUsageManager.setProjectId(currentProject.id)
          console.log('🔧 Ensured LLM usage manager has project ID:', currentProject.id)
        }
        
        // Show loading modal
        this.uiManager.showLoadingModal('Processing Your Descriptions', 'OCR and AI processing happening in the background...')
        
        // Refresh plot viewer to show loading state instead of empty coordinates message
        const plotViewer = document.querySelector('#plot-viewer') as any
        if (plotViewer && plotViewer.plotViewer && plotViewer.plotViewer.refresh) {
          plotViewer.plotViewer.refresh()
          console.log('🔄 Refreshed plot viewer to show loading state')
        }
        
        try {
          // Start background processing workflow
          await this.processingManager.startBackgroundProcessing()
        } catch (error) {
          console.error('❌ Background processing failed:', error)
          this.uiManager.hideLoadingModal()
          this.toast.error('Processing Failed', 'Failed to process descriptions. Please try again.')
        }
      })
    }
  }

  private getDocumentName(documentId: string, documentType?: string): string {
    // Handle manual entries specially
    if (documentType === 'manual' || documentId === 'manual-entry') {
      return 'Manual Text Entry'
    }
    
    const docs = this.appState.getProjectDocuments()
    // Handle both legacy pdf_id and new document_id
    const doc = docs.find(d => d.id === documentId)
    return doc?.original_filename || 'Unknown Document'
  }
}
